import React from 'react';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { useModelStore } from 'state/store/chat/use-model-store';
import { useCustomGptStore } from 'state/store/chat/use-custom-gpt-store';
import { ASSISTANT_ICONS } from '../../constants/assistant-icons';

export const ConfigureTab = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const models = useModelStore((state) => state.models);

    const {
        name,
        setName,
        systemPrompt,
        setSystemPrompt,
        modelName,
        setModelName,
        temperature,
        setTemperature,
        logoUrl: logoId,
        setLogoUrl: setLogoId
    } = useCustomGptStore();

    return (
        <div className="flex flex-col gap-5">
            {/* Icon Selection */}
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start gap-1">
                    <Label className={`${theme === 'dark' ? 'text-typography-white' : 'text-typography-black-primary'} text-lg font-semibold leading-7 break-words`}>
                        {t('ICON') || 'Icon'}
                    </Label>
                    <p className={`${theme === 'dark' ? 'text-typography-grey-2-secondary-stroke' : 'text-typography-grey-1-secondary'} text-xs font-normal leading-4 break-words`}>
                        {t('CHOOSE_ICON_DESCRIPTION') || 'Choose an emoji or upload a custom icon'}
                    </p>
                </div>
                <div className="w-full flex items-center gap-2 flex-wrap">
                    {ASSISTANT_ICONS.map((icon) => (
                        <button
                            key={icon.id}
                            onClick={() => setLogoId(icon.id)}
                            className={`w-10 h-10 p-1.5 rounded-lg border hover:border-primary transition-all flex justify-center items-center ${logoId === icon.id
                                ? 'border-primary'
                                : 'border-transparent'
                                }`}
                        >
                            <img className='w-full h-full object-contain' src={icon.src} alt={icon.id} />
                        </button>
                    ))}
                    {/* <button className="w-10 h-10 p-1.5 rounded-lg border border-dashed border-white/20 flex justify-center items-center hover:border-[#b49b78] hover:bg-white/5 transition-all">
                        <span className="text-gray-400 text-xs">+</span>
                    </button> */}
                </div>
            </div>

            {/* Name Input */}
            <div>
                <Label htmlFor="assistant-name" className={`text-xs font-semibold uppercase leading-[18px] break-words ${theme === 'dark' ? 'text-typography-white' : 'text-typography-black-primary'}`}>
                    {t('NAME_LABEL') || 'NAME'} <span className="text-red-500 text-xs font-semibold uppercase leading-[18px] break-words">*</span>
                </Label>
                <Input
                    id="assistant-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('NAME_PLACEHOLDER') || 'Name of the assistant'}
                    className={
                        `w-full px-3 py-[10px] rounded-[6px] text-xs font-normal leading-[18px] ${theme === 'dark' ? 'bg-[#1C1C1F] text-typography-white-stroke placeholder:text-typography-white-stroke border border-primary focus:outline-none focus:ring-0' : 'bg-typography-white text-black-500 placeholder:text-black-500 border border-[#D9D9D9]'}`
                    }
                />

            </div>

            {/* System Instructions Input */}
            <div>
                <Label htmlFor="system-instructions" className={`text-xs font-semibold uppercase leading-[18px] break-words ${theme === 'dark' ? 'text-typography-white' : 'text-typography-black-primary'}`}>
                    {t('SYSTEM_INSTRUCTIONS_LABEL') || 'SYSTEM INSTRUCTIONS'} <span className="text-red-500 text-xs font-semibold uppercase leading-[18px] break-words">*</span>
                </Label>
                <Textarea
                    id="system-instructions"
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    placeholder={t('SYSTEM_INSTRUCTIONS_PLACEHOLDER') || 'You are an expert code reviewer...'}
                    className={
                        `w-full px-3 py-[10px] rounded-[6px] text-xs font-normal leading-[18px] ${theme === 'dark' ? 'bg-[#1C1C1F] text-typography-white-stroke placeholder:text-typography-white-stroke border border-primary focus:outline-none focus:ring-0' : 'bg-typography-white text-black-500 placeholder:text-black-500 border border-[#D9D9D9]'}`
                    }
                />
            </div>

            {/* Model Selection */}
            <div>
                <Label className={`text-xs font-semibold uppercase leading-[18px] break-words ${theme === 'dark' ? 'text-typography-white' : 'text-typography-black-primary'}`}>
                    {t('MODEL_LABEL') || 'MODEL'} <span className="text-red-500 text-xs font-semibold uppercase leading-[18px] break-words">*</span>
                </Label>
                <Select value={modelName} onValueChange={setModelName}>
                    <SelectTrigger className={
                        `w-full px-3 py-[10px] rounded-[6px] text-xs font-normal leading-[18px] ${theme === 'dark' ? 'bg-[#1C1C1F] text-typography-white-stroke placeholder:text-typography-white-stroke border border-primary focus:outline-none focus:ring-0' : 'bg-typography-white text-black-500 placeholder:text-black-500 border border-[#D9D9D9] focus:outline-none focus:ring-0'}`
                    }>
                        <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent className={theme === 'dark' ? 'bg-[#1C1C1F]' : 'bg-typography-white'}>
                        {/* <SelectItem value="claude-3-5">Claude Sonnet 3.5</SelectItem>
                        <SelectItem value="claude-4-5">Claude Sonnet 4.5</SelectItem>
                        <SelectItem value="gpt-4o">GPT-4o</SelectItem> */}
                        {models.filter((model) => model.id !== 'auto').map((model) => (
                            <SelectItem key={model.id} value={model.id}>{model.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Creativity Slider */}
            <div>
                <div className="flex justify-between items-center">
                    <Label className={`text-xs font-semibold uppercase leading-[18px] break-words ${theme === 'dark' ? 'text-typography-white' : 'text-typography-black-primary'}`}>
                        {t('CREATIVITY_TEMPERATURE') || 'Creativity (Temperature)'}
                    </Label>
                </div>
                <div className="relative pt-2">
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={temperature}
                        onChange={(e) => setTemperature(parseFloat(e.target.value))}
                        className="w-full h-1 bg-[#2a2a2a] rounded-lg appearance-none cursor-pointer accent-[#b49b78]"
                        style={{
                            background: `linear-gradient(to right, ${theme === 'dark' ? '#AC9578' : '#CAB294'} ${temperature * 100}%, #2a2a2a ${temperature * 100}%)`
                        }}
                    />
                    <div className={`flex justify-between mt-2 text-xs font-normal leading-[18px] break-words ${theme === 'dark' ? 'text-typography-grey-2-secondary-stroke' : 'text-typography-grey-1-secondary'}`}>
                        <span>0.0 ({t('FOCUSED') || 'Focused'})</span>
                        <span>{temperature}</span>
                        <span>1.0 ({t('CREATIVE') || 'Creative'})</span>
                    </div>
                    <p className={`text-xs font-normal leading-[18px] break-words mt-2 ${theme === 'dark' ? 'text-typography-white-stroke' : 'text-typography-grey-2-secondary'}`}>
                        {t('AUTO_ADJUST_KNOWLEDGE_BASE') || 'Note: Automatically adjust to 0.3 when knowledge base is attached'}
                    </p>
                </div>
            </div>
        </div >
    );
};
